// src/utils/webVitals.js
// Reports Core Web Vitals (LCP, INP, CLS, FCP, TTFB) to both
// Firebase Performance custom traces and Firestore for dashboarding.

import { trace } from 'firebase/performance';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, perf, auth } from '../firebase';

/**
 * Send a single Web Vital metric to Firebase Performance as a custom trace
 * and persist it to Firestore for historical tracking.
 */
function sendMetric({ name, value, rating, id }) {
  // 1. Firebase Performance custom trace
  try {
    const t = trace(perf, `web_vital_${name}`);
    t.start();
    t.putMetric('value', Math.round(value));
    t.putAttribute('rating', rating); // "good" | "needs-improvement" | "poor"
    t.putAttribute('path', window.location.pathname);
    t.stop();
  } catch {
    // Silently ignore — perf SDK may not be ready yet
  }

  // 2. Persist to Firestore `webVitals` collection
  try {
    const user = auth.currentUser;
    addDoc(collection(db, 'webVitals'), {
      name,
      value: Math.round(value * 100) / 100,
      rating,
      metricId: id,
      path: window.location.pathname,
      userId: user?.uid || null,
      userAgent: navigator.userAgent,
      connection: navigator.connection?.effectiveType || null,
      timestamp: serverTimestamp(),
    }).catch(() => {});
  } catch {
    // Silently fail
  }
}

/**
 * Initialize Web Vitals collection.
 * Call once at app startup (index.js). Dynamically imports web-vitals
 * to avoid blocking the critical path.
 */
export function initWebVitals() {
  import('web-vitals').then(({ onLCP, onINP, onCLS, onFCP, onTTFB }) => {
    onLCP(sendMetric);
    onINP(sendMetric);
    onCLS(sendMetric);
    onFCP(sendMetric);
    onTTFB(sendMetric);
  });
}
